import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import useAuthStore from './store/authStore';
import { startOfflineSync, installOnlineListeners } from './offline/sync';
import Layout from './layouts/Layout';
import LoginPage from './pages/auth/LoginPage';
import Dashboard from './pages/Dashboard';
import MedicinesPage from './pages/medicines/MedicinesPage';
import InventoryPage from './pages/inventory/InventoryPage';
import PurchasesPage from './pages/purchases/PurchasesPage';
import SalesPage from './pages/sales/SalesPage';
import SaleReturnsPage from './pages/saleReturns/SaleReturnsPage';
import PurchaseReturnsPage from './pages/purchaseReturns/PurchaseReturnsPage';
import CustomersPage from './pages/customers/CustomersPage';
import SuppliersPage from './pages/suppliers/SuppliersPage';
import PatientsPage from './pages/patients/PatientsPage';
import EmployeesPage from './pages/employees/EmployeesPage';
import PrescriptionsPage from './pages/prescriptions/PrescriptionsPage';
import BranchesPage from './pages/branches/BranchesPage';
import UsersPage from './pages/users/UsersPage';
import RolesPage from './pages/users/RolesPage';
import PartnersPage   from './pages/partners/PartnersPage';
import AccountingPage from './pages/accounting/AccountingPage';
import ExpensesPage from './pages/accounting/ExpensesPage';
import ReportsPage from './pages/reports/ReportsPage';
import AuditPage from './pages/audit/AuditPage';
import NotFoundPage from './pages/NotFoundPage';
import MasterDataPage from './pages/masterdata/MasterDataPage';
import BankAccountsPage from './pages/bank/BankAccountsPage';
import RequisitionsPage from './pages/requisitions/RequisitionsPage';
import PurchaseRequestsPage from './pages/purchaseRequests/PurchaseRequestsPage';
import SettingsPage from './pages/settings/SettingsPage';
import BackupSettings from './pages/settings/BackupSettings';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

function App() {
  const { isAuthenticated, fetchMe } = useAuthStore();
  useEffect(() => { if (isAuthenticated) fetchMe(); }, [isAuthenticated, fetchMe]);

  // These two were defined in offline/sync.js but never actually wired up
  // anywhere, so the full offline cache (medicines, inventory, customers,
  // suppliers, categories, settings) was never bulk-downloaded — it only
  // ever got filled in piecemeal, page by page, as a side effect of normal
  // browsing while online. That's why clearing the cache/IndexedDB left
  // offline mode with nothing to show: there was no mechanism left to
  // rebuild it in one shot. This runs the full sync once on login (if
  // online) and again every time the connection comes back.
  useEffect(() => {
    if (!isAuthenticated) return;
    startOfflineSync();
    const cleanup = installOnlineListeners();
    return cleanup;
  }, [isAuthenticated]);

  return (
    <Router>
      <Toaster position="top-right" toastOptions={{ duration: 3000 }} />
      <Routes>
        <Route path="/login" element={!isAuthenticated ? <LoginPage /> : <Navigate to="/" replace />} />
        <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
          <Route index element={<Dashboard />} />
          <Route path="medicines" element={<MedicinesPage />} />
          <Route path="inventory" element={<InventoryPage />} />
          <Route path="purchases" element={<PurchasesPage />} />
          <Route path="purchase-requests" element={<PurchaseRequestsPage />} />
          <Route path="requisitions" element={<RequisitionsPage />} />
          <Route path="sales" element={<SalesPage />} />
          <Route path="sale-returns" element={<SaleReturnsPage />} />
          <Route path="purchase-returns" element={<PurchaseReturnsPage />} />
          <Route path="customers" element={<CustomersPage />} />
          <Route path="suppliers" element={<SuppliersPage />} />
          <Route path="patients" element={<PatientsPage />} />
          <Route path="employees" element={<EmployeesPage />} />
          <Route path="prescriptions" element={<PrescriptionsPage />} />
          <Route path="branches" element={<BranchesPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="roles" element={<RolesPage />} />
          <Route path="accounting" element={<AccountingPage />} />
          <Route path="partners" element={<PartnersPage />} />
          <Route path="bank-accounts" element={<BankAccountsPage />} />
          <Route path="expenses" element={<ExpensesPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="audit" element={<AuditPage />} />
          <Route path="master-data" element={<MasterDataPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="settings/backup" element={<BackupSettings />} />
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Router>
  );
}

export default App;
